import { Component } from '@theme/component';

/**
 * Client-side filtering and sorting for the Exhibiting Artists grid: an A-Z
 * name filter (single-select, click again to clear) combined with any
 * number of Medium/Region/Availability checkbox filters (OR within a
 * group, AND across groups), plus an A-Z/Z-A sort. Nothing here talks to
 * the server - every artist card is already in the DOM (server-rendered
 * A-Z by default); filtering just toggles `hidden` and sorting just
 * reorders the existing cards. Every filter/sort dropdown closes on an
 * outside click, and selected Medium/Region/Availability values also show
 * as removable chips below the filter row.
 *
 * @extends {Component<{grid: HTMLElement, lettersScroller: HTMLElement, lettersScrollButton: HTMLElement, emptyState: HTMLElement, activeFilters: HTMLElement, filterDetails?: HTMLDetailsElement[]}>}
 */
class ArtistIndexComponent extends Component {
  requiredRefs = ['grid', 'lettersScroller', 'lettersScrollButton', 'emptyState', 'activeFilters'];

  /** @type {string | null} */
  #activeLetter = null;

  /** @type {'asc' | 'desc'} */
  #sortDirection = 'asc';

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);
    document.addEventListener('click', this.#handleDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
    document.removeEventListener('click', this.#handleDocumentClick);
  }

  /**
   * Closes any open filter/sort dropdown when a click lands outside of it -
   * native <details> only closes on re-clicking its own summary otherwise.
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    for (const details of this.refs.filterDetails ?? []) {
      if (details.open && event.target instanceof Node && !details.contains(event.target)) {
        details.removeAttribute('open');
      }
    }
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const letterButton = target.closest('.fairplay-artist-index__letter');
    if (letterButton instanceof HTMLButtonElement) {
      const { letter } = letterButton.dataset;
      this.#activeLetter = this.#activeLetter === letter ? null : (letter ?? null);
      this.#updateLetterButtons();
      this.#applyFilters();
      return;
    }

    if (target.closest('.fairplay-artist-index__letters-scroll')) {
      this.refs.lettersScroller.scrollBy({ left: 160, behavior: 'smooth' });
      return;
    }

    const chip = target.closest('.fairplay-artist-index__active-filter');
    if (chip instanceof HTMLElement) {
      const { filterGroup, filterValue } = chip.dataset;

      if (filterGroup === 'letter') {
        this.#activeLetter = null;
        this.#updateLetterButtons();
      } else {
        for (const checkbox of this.querySelectorAll('input[data-filter-group]')) {
          if (
            checkbox instanceof HTMLInputElement &&
            checkbox.dataset.filterGroup === filterGroup &&
            checkbox.value === filterValue
          ) {
            checkbox.checked = false;
          }
        }
      }

      this.#applyFilters();
    }
  };

  /** @param {Event} event */
  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    if (event.target.dataset.filterGroup) {
      this.#applyFilters();
      return;
    }

    if (event.target.name === 'artist-sort' && event.target.dataset.sort) {
      this.#sortDirection = event.target.dataset.sort === 'desc' ? 'desc' : 'asc';
      this.#applySort();
      event.target.closest('details')?.removeAttribute('open');
    }
  };

  #updateLetterButtons() {
    const buttons = this.querySelectorAll('.fairplay-artist-index__letter');
    for (const button of buttons) {
      const isActive = button instanceof HTMLButtonElement && button.dataset.letter === this.#activeLetter;
      button.setAttribute('aria-pressed', String(isActive));
    }
  }

  /** @returns {Record<string, string[]>} */
  #selectedFilters() {
    /** @type {Record<string, string[]>} */
    const selected = {};

    for (const details of this.refs.filterDetails ?? []) {
      for (const checkbox of details.querySelectorAll('input[type="checkbox"]')) {
        if (checkbox instanceof HTMLInputElement && checkbox.checked && checkbox.dataset.filterGroup) {
          const group = checkbox.dataset.filterGroup;
          selected[group] ??= [];
          selected[group].push(checkbox.value);
        }
      }
    }

    return selected;
  }

  #applyFilters() {
    const selected = this.#selectedFilters();
    const cards = this.refs.grid.querySelectorAll('.fairplay-artist-index__card');
    let visibleCount = 0;

    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;

      const matchesLetter = !this.#activeLetter || card.dataset.letter === this.#activeLetter;

      const matchesFilters = Object.entries(selected).every(([group, values]) => {
        if (values.length === 0) return true;

        // A card can carry several comma-separated values in one attribute -
        // an artist working in "Painting, Sculpture" should come back under
        // either. Matching the whole string would only ever match a
        // checkbox labelled with the same combination, so compare the parts.
        const cardValues = (card.dataset[group] ?? '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean);

        return cardValues.some((value) => values.includes(value));
      });

      const visible = matchesLetter && matchesFilters;
      card.hidden = !visible;
      if (visible) visibleCount += 1;
    }

    this.refs.emptyState.hidden = visibleCount > 0;
    this.#renderActiveFilters(selected);
  }

  /**
   * Renders one removable chip per currently-selected Medium/Region/
   * Availability value, plus the active A-Z letter if one is set - it has
   * no other visible "clear" affordance beyond re-clicking the same letter
   * in the (possibly scrolled-away) letters row, which isn't discoverable.
   * (Sort isn't included here - it's always exactly one of A-Z/Z-A, not
   * something to clear.)
   * @param {Record<string, string[]>} selected
   */
  #renderActiveFilters(selected) {
    const container = this.refs.activeFilters;
    container.replaceChildren();

    if (this.#activeLetter) {
      container.append(this.#createFilterChip('letter', this.#activeLetter));
    }

    for (const [group, values] of Object.entries(selected)) {
      for (const value of values) {
        container.append(this.#createFilterChip(group, value));
      }
    }

    container.hidden = container.childElementCount === 0;
  }

  /**
   * @param {string} group
   * @param {string} value
   * @returns {HTMLButtonElement}
   */
  #createFilterChip(group, value) {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'fairplay-artist-index__active-filter';
    chip.dataset.filterGroup = group;
    chip.dataset.filterValue = value;
    chip.setAttribute('aria-label', `Remove ${value} filter`);

    const label = document.createElement('span');
    label.textContent = value;

    const remove = document.createElement('span');
    remove.className = 'fairplay-artist-index__active-filter-remove';
    remove.setAttribute('aria-hidden', 'true');
    remove.textContent = '×';

    chip.append(label, remove);
    return chip;
  }

  /**
   * Re-orders the grid's existing cards by name. Everything's already in
   * the DOM (server-rendered A-Z by default), so this only ever reorders -
   * it doesn't add, remove, or affect which cards are hidden by filters.
   */
  #applySort() {
    const cards = [...this.refs.grid.querySelectorAll('.fairplay-artist-index__card')].filter(
      (card) => card instanceof HTMLElement
    );

    cards.sort((a, b) => {
      const comparison = (a.dataset.name ?? '').localeCompare(b.dataset.name ?? '');
      return this.#sortDirection === 'desc' ? -comparison : comparison;
    });

    for (const card of cards) {
      this.refs.grid.append(card);
    }
  }
}

if (!customElements.get('artist-index-component')) {
  customElements.define('artist-index-component', ArtistIndexComponent);
}
